import { ChangeDetectorRef, Component, forwardRef, Input, OnDestroy, OnInit, input, inject } from '@angular/core';
import {
  FormBuilder,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { AbstractControlValueAccessor } from '@app/shared/abstract_control_value_accessor.abstract';
import { SchemaTranslations } from '@app/shared/models/schema-translations';
import { Schema } from '@app/core/_models/schema';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getMainLanguage } from '@app/shared/utils/browser-checks';
import {
  DatePart,
  DatePartConfig,
  getDatePartMaxValue,
  getDatePartMinValue,
  getDatePartsConfig,
  mapDatePartToString,
} from './date-part';

import { SchemaTranslationsGetFieldTranslationsPipe } from '@app/+profile-form/components/properties-form/property-composite-date/schema-translations-get-field-translations.pipe';
import { PropertyIsFieldRequiredPipe } from '@app/+profile-form/components/properties-form/property-is-field-required.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { InputErrorsComponent } from '@app/shared/components/input-errors/input-errors.component';

@Component({
  selector: 'app-property-composite-date',
  templateUrl: './property-composite-date.component.html',
  styleUrls: ['./property-composite-date.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PropertyCompositeDateComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => PropertyCompositeDateComponent), multi: true },
  ],
  imports: [
    SchemaTranslationsGetFieldTranslationsPipe,
    PropertyIsFieldRequiredPipe,
    ReactiveFormsModule,
    TranslatePipe,
    InputErrorsComponent
],
})
export class PropertyCompositeDateComponent extends AbstractControlValueAccessor implements OnInit, OnDestroy, Validator {
  private cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  public set controlName(value: string) {
    this.controlNameValue = value;

    const isBirthday = value === 'birthday';
    this.dateInputsConfig = getDatePartsConfig(getMainLanguage(), isBirthday);
    this.dateFormGroup.controls.year.addValidators([
      Validators.min(getDatePartMinValue(DatePart.year, isBirthday)),
      Validators.max(getDatePartMaxValue(DatePart.year, isBirthday)),
    ]);
  }

  public readonly properties = input<Schema>(undefined!);
  public readonly schemaTranslations = input<SchemaTranslations | null>(undefined!);
  public readonly errors = input<any>();
  public readonly submitted = input(false);

  public dateFormGroup = this.fb.group({
    day: [null as number | null, [Validators.min(getDatePartMinValue(DatePart.day)), Validators.max(31)]],
    month: [null as number | null, [Validators.min(getDatePartMinValue(DatePart.month)), Validators.max(12)]],
    year: [null as number | null],
  });

  public dateInputsConfig: DatePartConfig[] = [];
  public controlNameValue;

  private unsubscribe$ = new Subject();

  public ngOnInit(): void {
    this.dateFormGroup.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(() => this.formControlChange());
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  public writeValue(date: string): void {
    if (!date) { return; }

    this.fillFieldWithCorrectDate(date);
    this.cdr.markForCheck();
  }

  public validate(): ValidationErrors | any {
    return {
      ...this.dateFormGroup.controls.day.errors ? { day: this.dateFormGroup.controls.day.errors } : {},
      ...this.dateFormGroup.controls.month.errors ? { month: this.dateFormGroup.controls.month.errors } : {},
      ...this.dateFormGroup.controls.year.errors ? { year: this.dateFormGroup.controls.year.errors } : {},
    };
  }

  private formControlChange(): void {
    this.onTouched();
    this.onChange(this.parsedInputDateForBackend());
  }

  private fillFieldWithCorrectDate(date: string): void {
    const [year, month, day] = date.split('-');
    this.dateFormGroup.patchValue({ year: +year, month: +month, day: +day });
  }

  private parsedInputDateForBackend(): string | null {
    const { year, month, day } = this.dateFormGroup.value;
    if (year && month && day) {
      return `${year}-${mapDatePartToString(month)}-${mapDatePartToString(day)}`;
    }
    return null;
  }
}
